(() => {
  const COLOR_KEYWORDS = ["cor", "color"];
  const SIZE_KEYWORDS = ["tamanho", "size"];
  const CART_STORAGE_KEY = "verosenso-buy-together-cart-id";

  const toSwatchClassSuffix = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/"/g, "'")
      .replace(/'/g, "-");

  class StorefrontClient {
    constructor({ token, apiVersion }) {
      this.token = token;
      this.apiVersion = apiVersion || "2025-01";
      this.endpoint = `/api/${this.apiVersion}/graphql.json`;
    }

    async graphql(query, variables = {}) {
      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": this.token,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        throw new Error("Falha na requisição para Storefront API.");
      }

      const payload = await response.json();
      if (payload.errors?.length) {
        throw new Error(payload.errors[0].message || "Erro inesperado no GraphQL.");
      }

      return payload.data;
    }

    getStoredCartId() {
      try {
        return window.localStorage.getItem(CART_STORAGE_KEY);
      } catch (error) {
        return null;
      }
    }

    setStoredCartId(cartId) {
      try {
        window.localStorage.setItem(CART_STORAGE_KEY, cartId);
      } catch (error) {
        return;
      }
    }

    clearStoredCartId() {
      try {
        window.localStorage.removeItem(CART_STORAGE_KEY);
      } catch (error) {
        return;
      }
    }

    async createCart() {
      const mutation = `
        mutation cartCreate {
          cartCreate {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const data = await this.graphql(mutation);
      const result = data?.cartCreate;
      if (!result || result.userErrors?.length) {
        throw new Error(result?.userErrors?.[0]?.message || "Não foi possível criar o carrinho.");
      }

      this.setStoredCartId(result.cart.id);
      return result.cart;
    }

    async addLines(cartId, lines) {
      const mutation = `
        mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const data = await this.graphql(mutation, { cartId, lines });
      const result = data?.cartLinesAdd;
      if (!result) {
        throw new Error("Resposta inválida ao adicionar itens no carrinho.");
      }

      if (result.userErrors?.length) {
        throw new Error(result.userErrors[0].message || "Não foi possível adicionar os itens.");
      }

      return result.cart;
    }

    async addVariantsAndGetCheckoutUrl(variantGids) {
      const lines = variantGids.map((merchandiseId) => ({
        merchandiseId,
        quantity: 1,
      }));

      let cartId = this.getStoredCartId();
      if (!cartId) {
        const cart = await this.createCart();
        cartId = cart.id;
      }

      try {
        const cart = await this.addLines(cartId, lines);
        return cart.checkoutUrl;
      } catch (error) {
        this.clearStoredCartId();
        const newCart = await this.createCart();
        const updatedCart = await this.addLines(newCart.id, lines);
        return updatedCart.checkoutUrl;
      }
    }
  }

  class VariantResolver {
    constructor(productData) {
      this.productData = productData;
      this.colorIndex = this.findOptionIndex(COLOR_KEYWORDS);
      this.sizeIndex = this.findOptionIndex(SIZE_KEYWORDS);
    }

    findOptionIndex(keywords) {
      const options = this.productData.options || [];
      const option = options.find((entry) => {
        const optionName = String(entry.name || "").toLowerCase();
        return keywords.some((keyword) => optionName.includes(keyword));
      });

      if (!option) return -1;
      return Number(option.position) - 1;
    }

    getColor(variant) {
      if (this.colorIndex < 0) return "";
      return variant.options?.[this.colorIndex] || "";
    }

    getSize(variant) {
      if (this.sizeIndex < 0) return "";
      return variant.options?.[this.sizeIndex] || "";
    }

    getColors() {
      const values = (this.productData.variants || [])
        .map((variant) => this.getColor(variant))
        .filter(Boolean);
      return [...new Set(values)];
    }

    getSizesByColor(color) {
      const variants = (this.productData.variants || []).filter((variant) => {
        if (this.colorIndex < 0) return variant.available;
        return this.getColor(variant) === color && variant.available;
      });

      const sizes = variants.map((variant) => this.getSize(variant)).filter(Boolean);
      return [...new Set(sizes)];
    }

    findVariant({ color, size }) {
      return (this.productData.variants || []).find((variant) => {
        const sameColor = this.colorIndex < 0 || this.getColor(variant) === color;
        const sameSize = this.sizeIndex < 0 || this.getSize(variant) === size;
        return sameColor && sameSize;
      });
    }

    findFirstVariantByColor(color) {
      if (!color) return this.findDefaultVariant();

      const byColor = (this.productData.variants || []).filter((variant) => {
        if (this.colorIndex < 0) return true;
        return this.getColor(variant) === color;
      });

      return byColor.find((variant) => variant.available) || byColor[0] || this.findDefaultVariant();
    }

    findDefaultVariant() {
      return (this.productData.variants || []).find((variant) => variant.available) || this.productData.variants?.[0];
    }
  }

  class ProductItemController {
    constructor(root, currencyCode, onStateChange) {
      this.root = root;
      this.currencyCode = currencyCode || "BRL";
      this.onStateChange = onStateChange;
      this.priceElement = this.root.querySelector("[data-buy-together-price]");
      this.colorsElement = this.root.querySelector("[data-buy-together-colors]");
      this.sizeSelect = this.root.querySelector("[data-buy-together-size-select]");
      this.imageElement = this.root.querySelector(".wrapper_custom_buy-together-content-product-image");

      const jsonScript = this.root.querySelector("[data-buy-together-product-json]");
      this.productData = jsonScript ? JSON.parse(jsonScript.textContent) : null;
      this.resolver = new VariantResolver(this.productData || { variants: [], options: [] });
      this.state = {
        color: "",
        size: "",
        variant: null,
        displayVariant: null,
      };
      this.defaultVariant = null;
    }

    init() {
      if (!this.productData?.variants?.length) return;

      this.defaultVariant = this.resolver.findDefaultVariant();
      if (!this.defaultVariant) return;

      this.state.color = this.resolver.getColor(this.defaultVariant) || this.resolver.getColors()[0] || "";
      this.state.size = "";
      this.state.variant = null;
      this.state.displayVariant = this.resolver.findFirstVariantByColor(this.state.color);

      this.renderColorSelector();
      this.renderSizeSelector();
      this.bindEvents();
      this.syncUI();
      this.notifyStateChange();
    }

    bindEvents() {
      this.colorsElement?.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-buy-together-color]");
        if (!trigger || trigger.hasAttribute("disabled")) return;

        const selectedColor = trigger.getAttribute("data-buy-together-color");
        this.state.color = selectedColor || "";
        this.state.size = "";
        this.state.variant = null;
        this.state.displayVariant = this.resolver.findFirstVariantByColor(this.state.color);

        this.renderColorSelector();
        this.renderSizeSelector();
        this.syncUI();
        this.notifyStateChange();
      });

      this.sizeSelect?.addEventListener("change", (event) => {
        this.state.size = event.target.value || "";
        if (!this.state.size) {
          this.state.variant = null;
          this.syncUI();
          this.notifyStateChange();
          return;
        }

        this.state.variant = this.resolver.findVariant({
          color: this.state.color,
          size: this.state.size,
        });
        if (!this.state.variant) {
          this.state.variant = null;
        }
        this.state.displayVariant = this.state.variant || this.resolver.findFirstVariantByColor(this.state.color);
        this.syncUI();
        this.notifyStateChange();
      });
    }

    renderColorSelector() {
      if (!this.colorsElement) return;

      const colors = this.resolver.getColors();
      if (!colors.length) {
        this.colorsElement.innerHTML = "";
        return;
      }

      const colorButtons = colors
        .map((color) => {
          const isActive = color === this.state.color;
          const sizesForColor = this.resolver.getSizesByColor(color);
          const disabledAttr = sizesForColor.length ? "" : "disabled";
          const classes = [
            "wrapper_custom_buy-together-content-product-info-color",
            isActive ? "active" : "",
          ]
            .join(" ")
            .trim();

          const swatchClass = `custom__color-swatches--${toSwatchClassSuffix(color)}`;

          return `
            <button
              class="${classes}"
              type="button"
              data-buy-together-color="${color}"
              ${disabledAttr}
              aria-label="${color}"
            >
              <span class="wrapper_custom_buy-together-content-product-info-color-name-tooltip">${color}</span>
              <span class="wrapper_custom_buy-together-content-product-info-color-hex ${swatchClass}"></span>
            </button>
          `;
        })
        .join("");

      this.colorsElement.innerHTML = colorButtons;
    }

    renderSizeSelector() {
      if (!this.sizeSelect) return;

      const hasSizeOption = this.resolver.sizeIndex >= 0;
      if (!hasSizeOption) {
        this.sizeSelect.innerHTML = `<option value="">Tamanho único</option>`;
        this.sizeSelect.disabled = false;
        return;
      }

      const sourceVariants = (this.productData.variants || []).filter((variant) => {
        if (!variant.available) return false;
        if (!this.state.color) return true;
        return this.resolver.getColor(variant) === this.state.color;
      });

      const sizes = [...new Set(sourceVariants.map((variant) => this.resolver.getSize(variant)).filter(Boolean))];
      if (!sizes.length) {
        this.sizeSelect.innerHTML = `<option value="">Sem estoque</option>`;
        this.sizeSelect.disabled = false;
        return;
      }

      this.sizeSelect.disabled = false;
      this.sizeSelect.innerHTML = [
        `<option value="">Selecione o tamanho</option>`,
        ...sizes.map((size) => {
          const selected = size === this.state.size ? "selected" : "";
          const optionLabel =
            size === this.state.size ? `Tamanho: ${size} selecionado` : size;
          return `<option value="${size}" ${selected}>${optionLabel}</option>`;
        }),
      ].join("");
    }

    syncUI() {
      const variantForDisplay = this.state.displayVariant || this.state.variant || this.defaultVariant;
      if (!variantForDisplay) return;

      if (this.priceElement) {
        this.priceElement.textContent = this.formatMoney(variantForDisplay.price);
      }

      if (this.imageElement && variantForDisplay.featuredImage) {
        this.imageElement.removeAttribute("srcset");
        this.imageElement.src = variantForDisplay.featuredImage;
      }
    }

    formatMoney(centsValue) {
      const numericValue = Number(centsValue || 0) / 100;
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: this.currencyCode,
      }).format(numericValue);
    }

    isSelectionComplete() {
      const requiresColor = this.resolver.colorIndex >= 0;
      const requiresSize = this.resolver.sizeIndex >= 0;
      const hasColor = !requiresColor || Boolean(this.state.color);
      const hasSize = !requiresSize || Boolean(this.state.size);
      return hasColor && hasSize && Boolean(this.state.variant?.id);
    }

    notifyStateChange() {
      if (typeof this.onStateChange === "function") {
        this.onStateChange();
      }
    }

    getSelectedVariantGid() {
      if (!this.isSelectionComplete()) return null;
      const variantId = String(this.state.variant.id);
      return `gid://shopify/ProductVariant/${variantId}`;
    }
  }

  class BuyTogetherController {
    constructor(section) {
      this.section = section;
      this.token = section.dataset.storefrontToken || "";
      this.apiVersion = section.dataset.storefrontApiVersion || "2025-01";
      this.currencyCode = section.dataset.currencyCode || "BRL";
      this.submitButton = section.querySelector("[data-buy-together-submit]");
      this.itemElements = Array.from(section.querySelectorAll("[data-buy-together-item]"));
      this.itemControllers = [];
      this.client = new StorefrontClient({
        token: this.token,
        apiVersion: this.apiVersion,
      });
    }

    init() {
      if (!this.token || !this.submitButton || !this.itemElements.length) return;

      this.itemControllers = this.itemElements.map(
        (item) => new ProductItemController(item, this.currencyCode, this.updateSubmitState.bind(this))
      );
      this.itemControllers.forEach((controller) => controller.init());
      this.submitButton.addEventListener("click", this.handleSubmit.bind(this));
      this.updateSubmitState();
    }

    updateSubmitState() {
      const canSubmit =
        this.itemControllers.length > 0 &&
        this.itemControllers.every((controller) => controller.isSelectionComplete());
      this.submitButton.disabled = !canSubmit;
    }

    async handleSubmit() {
      const variantGids = this.itemControllers
        .map((controller) => controller.getSelectedVariantGid())
        .filter(Boolean);

      if (!variantGids.length) return;

      const initialText = this.submitButton.textContent;
      this.submitButton.disabled = true;
      this.submitButton.textContent = "adicionando...";

      try {
        const checkoutUrl = await this.client.addVariantsAndGetCheckoutUrl(variantGids);
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        throw new Error("Checkout indisponível.");
      } catch (error) {
        this.submitButton.textContent = "tentar novamente";
      } finally {
        this.submitButton.disabled = false;
        if (this.submitButton.textContent === "adicionando...") {
          this.submitButton.textContent = initialText;
        }
      }
    }
  }

  const init = () => {
    const sections = document.querySelectorAll("[data-buy-together-section]");
    sections.forEach((section) => {
      const controller = new BuyTogetherController(section);
      controller.init();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
